"use client";

import {
  Box,
  Button,
  HStack,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithMenu from "@/shared/ui/actions/WithMenu";
import WithMenuItem from "@/shared/ui/actions/WithMenuItem";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import type {
  StudioQuickAction,
  StudioSituationLoadManagerItem,
} from "./useStudioScreen";

type StudioSituationLoadManagerProps = {
  action: StudioQuickAction;
  availableCount: number;
  items: StudioSituationLoadManagerItem[];
  loadedCount: number;
  onLoadAll: () => void;
  onSelectVisible: (actionKey: StudioQuickAction["key"]) => Promise<void>;
  onSituationLoadChange: (situationId: string, isLoaded: boolean) => void;
};

function formatLoadedCount(loadedCount: number, availableCount: number) {
  if (availableCount === 0) {
    return "No situations in this perspective";
  }

  return `${loadedCount} of ${availableCount} loaded`;
}

export default function StudioSituationLoadManager({
  action,
  availableCount,
  items,
  loadedCount,
  onLoadAll,
  onSelectVisible,
  onSituationLoadChange,
}: StudioSituationLoadManagerProps) {
  const hasHiddenSituations = loadedCount < availableCount;

  return (
    <HStack gap="0" flexShrink="0">
      <Button
        variant="outline"
        onClick={() => {
          void onSelectVisible(action.key);
        }}
        title={action.humanReadable}
        aria-label={action.humanReadable}
        borderColor="moveon.surface.interactiveBorder"
        borderEndRadius="0"
        color="moveon.surface.fg"
        h="10"
        w="10"
        minW="10"
        p="0"
        disabled={action.disabled}
        _hover={{
          bg: "moveon.surface.elevatedBg",
          borderColor: "moveon.selection.hoverBorder",
        }}
      >
        <GenericIcon
          icon={action.icon}
          size={iconSizePresets.actionButton}
          humanReadable={action.humanReadable}
          visuallyInactive={action.disabled}
        />
      </Button>

      <WithMenu
        placement="bottom-start"
        size="sm"
        minWidth="min(24rem, calc(100vw - 1rem))"
        maxWidth="calc(100vw - 1rem)"
        trigger={
          <Button
            variant="outline"
            title="Manage loaded situations"
            aria-label="Manage loaded situations"
            borderColor="moveon.surface.interactiveBorder"
            borderStartWidth="0"
            borderStartRadius="0"
            color="moveon.surface.fg"
            h="10"
            w="8"
            minW="8"
            p="0"
            _hover={{
              bg: "moveon.surface.elevatedBg",
              borderColor: "moveon.selection.hoverBorder",
            }}
          >
            <GenericIcon
              icon="expand_more"
              size="xs"
              humanReadable="Open loaded situations menu"
            />
          </Button>
        }
      >
        <Stack gap="3" p="3" minW="0" maxW="full" overflow="hidden">
          <Box minW="0">
            <Text fontSize="sm" fontWeight="semibold">
              Loaded situations
            </Text>
            <Text fontSize="xs" color="moveon.text.muted">
              {formatLoadedCount(loadedCount, availableCount)}
            </Text>
          </Box>

          {items.length === 0 ? (
            <Text fontSize="sm" color="moveon.text.muted">
              No situations in this perspective.
            </Text>
          ) : (
            <VStack align="stretch" gap="2" maxH="[22rem]" overflowY="auto">
              {items.map((item) => (
                <HStack
                  key={item.situationId}
                  align={{ base: "stretch", sm: "center" }}
                  justify="space-between"
                  gap={{ base: "2", sm: "3" }}
                  flexDirection={{ base: "column", sm: "row" }}
                  borderWidth="1"
                  borderColor="moveon.surface.border"
                  borderRadius="md"
                  bg={
                    item.isLoaded
                      ? "moveon.surface.toolbarBg"
                      : "moveon.surface.bg"
                  }
                  minW="0"
                  maxW="full"
                  w="full"
                  overflow="hidden"
                  px={{ base: "2.5", sm: "3" }}
                  py="2"
                >
                  <Box minW="0" maxW="full" flex="1" w="full">
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      lineHeight="short"
                      lineClamp={{ base: 2, sm: 1 }}
                      overflowWrap="anywhere"
                      wordBreak="break-word"
                      hyphens="auto"
                    >
                      {item.title}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="moveon.text.muted"
                      lineHeight="short"
                      lineClamp={{ base: 2, sm: 1 }}
                      overflowWrap="anywhere"
                      wordBreak="break-word"
                      hyphens="auto"
                    >
                      {item.description}
                    </Text>
                  </Box>

                  <Switch.Root
                    checked={item.isLoaded}
                    onCheckedChange={(details) => {
                      onSituationLoadChange(
                        item.situationId,
                        details.checked,
                      );
                    }}
                    aria-label={`${item.isLoaded ? "Hide" : "Load"} ${
                      item.title
                    }`}
                    flexShrink="0"
                    alignSelf={{ base: "flex-end", sm: "center" }}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control
                      bg={
                        item.isLoaded
                          ? "moveon.secondaryLight"
                          : "moveon.inactive"
                      }
                    >
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label fontSize="xs">
                      {item.isLoaded ? "Loaded" : "Hidden"}
                    </Switch.Label>
                  </Switch.Root>
                </HStack>
              ))}
            </VStack>
          )}

          <HStack justify="flex-end">
            <Box minW="28">
              <WithMenuItem
                value="load_all"
                humanReadable="Load all situations in this perspective"
                disabled={!hasHiddenSituations}
                onClick={onLoadAll}
                icon="load"
              >
                <Text fontSize="xs" fontWeight="medium">
                  Load all
                </Text>
              </WithMenuItem>
            </Box>
          </HStack>
        </Stack>
      </WithMenu>
    </HStack>
  );
}
